//src/components/steps/Step2PriceSpecs.jsx
import {
  MdAdd,
  MdAttachMoney,
  MdDeleteOutline,
  MdLocationOn,
  MdOutlineHomeWork,
  MdOutlinePlace,
} from "react-icons/md";
import PropertyImageUploader from "../PropertyImageUploader";
import { useSellProperty } from "../SellPropertyContext";
import { FormField, InputIcon, StepSection } from "../SellPropertyUI";
import {
  NEARBY_CATEGORY_OPTIONS,
  RENT_DURATION,
} from "../sellProperty.constants";

const RequiredMark = () => (
  <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>
);

const Step2PriceSpecs = () => {
  const {
    form,
    errors,
    handleChange,
    handlePriceChange,
    featureGroups,
    loadingData,
    handleToggleFeature,
    isFeatureSelected,
    propertyImages,
    setPropertyImages,
    imageError,
    loading,
    addNearbyPlace,
    updateNearbyPlace,
    removeNearbyPlace,
  } = useSellProperty();

  const visibleFeatureGroups = featureGroups.filter(
    (group) => Array.isArray(group.features) && group.features.length > 0,
  );

  return (
    <StepSection
      icon={<MdAttachMoney size={20} color="var(--color-primary)" />}
      title="Harga, Fitur & Lokasi Terdekat"
      desc="Tetapkan harga, pilih fitur properti, dan tambahkan lokasi penting di sekitar properti."
    >
      <div className="sell-grid">
        <FormField
          label={
            <>
              Harga (Rp) <RequiredMark />
            </>
          }
          error={errors.price}
        >
          <div className="sell-input-wrap">
            <InputIcon>
              <MdAttachMoney size={16} />
            </InputIcon>
            <input
              className={`mp-input sell-input-padded${errors.price ? " error" : ""}`}
              type="text"
              inputMode="numeric"
              name="price"
              placeholder={
                form.status === "for_rent"
                  ? "Contoh: 25.000.000"
                  : "Contoh: 850.000.000"
              }
              value={form.price}
              onChange={handlePriceChange}
            />
          </div>
        </FormField>

        {form.status === "for_rent" && (
          <FormField label="Durasi Sewa">
            <select
              className="mp-input"
              name="sale_rent_duration"
              value={form.sale_rent_duration}
              onChange={handleChange}
            >
              {RENT_DURATION.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </FormField>
        )}
      </div>

      <div className="sell-option-group">
        <label className="mp-label">Fitur Properti</label>
        <p className="sell-spec-hint">
          Pilih fitur yang tersedia pada properti Anda.
        </p>

        {loadingData ? (
          <p className="sell-loading-text">Memuat fitur properti...</p>
        ) : visibleFeatureGroups.length > 0 ? (
          <div style={{ display: "grid", gap: "16px" }}>
            {visibleFeatureGroups.map((group) => (
              <div
                key={group.id}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "16px",
                  padding: "16px",
                  background: "rgba(255,255,255,0.6)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "var(--color-text)" }}>
                    {group.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--color-text-muted)" }}>
                    {group.features.length} item
                  </div>
                </div>

                <div className="sell-spec-grid">
                  {group.features.map((feature) => {
                    const selected = isFeatureSelected(feature.id);

                    return (
                      <button
                        key={feature.id}
                        type="button"
                        className={`sell-spec-btn${selected ? " active" : ""}`}
                        onClick={() => handleToggleFeature(feature.id)}
                      >
                        <span className="sell-spec-icon">
                          <MdOutlineHomeWork size={20} />
                        </span>
                        <span className="sell-spec-label">{feature.name}</span>
                        {selected && <span className="sell-spec-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "14px 16px",
              border: "1px dashed var(--color-border)",
              borderRadius: "16px",
              color: "var(--color-text-muted)",
              background: "rgba(255,255,255,0.55)",
            }}
          >
            Belum ada fitur yang tersedia untuk ditampilkan.
          </div>
        )}

        {form.feature_ids?.length > 0 && (
          <p className="sell-spec-count">
            {form.feature_ids.length} fitur dipilih
          </p>
        )}
      </div>

      <div className="sell-option-group">
        <label className="mp-label">Lokasi Terdekat</label>
        <p className="sell-spec-hint">
          Tambahkan fasilitas penting di sekitar properti, seperti pendidikan,
          kesehatan, transportasi, dan pusat perbelanjaan.
        </p>

        <div style={{ display: "grid", gap: "12px" }}>
          {(form.nearby_places || []).map((place, index) => (
            <div
              key={`${index}-${place.sort_order}`}
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: "16px",
                padding: "16px",
                background: "rgba(255,255,255,0.62)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontWeight: 700 }}>Lokasi #{index + 1}</div>
                <button
                  type="button"
                  className="btn-outline-mp"
                  onClick={() => removeNearbyPlace(index)}
                  style={{ minWidth: "unset", padding: "10px 12px" }}
                >
                  <MdDeleteOutline size={16} />
                  Hapus
                </button>
              </div>

              <div className="sell-grid">
                <FormField label="Kategori">
                  <select
                    className="mp-input"
                    value={place.category || ""}
                    onChange={(e) =>
                      updateNearbyPlace(index, "category", e.target.value)
                    }
                  >
                    {NEARBY_CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value || "placeholder"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Nama Lokasi">
                  <div className="sell-input-wrap">
                    <InputIcon>
                      <MdOutlinePlace size={16} />
                    </InputIcon>
                    <input
                      className="mp-input sell-input-padded"
                      type="text"
                      placeholder="Contoh: Universitas Indonesia"
                      value={place.name || ""}
                      onChange={(e) =>
                        updateNearbyPlace(index, "name", e.target.value)
                      }
                    />
                  </div>
                </FormField>

                <FormField label="Alamat / Area">
                  <div className="sell-input-wrap">
                    <InputIcon>
                      <MdLocationOn size={16} />
                    </InputIcon>
                    <input
                      className="mp-input sell-input-padded"
                      type="text"
                      placeholder="Contoh: Cidadap, Bandung"
                      value={place.address || ""}
                      onChange={(e) =>
                        updateNearbyPlace(index, "address", e.target.value)
                      }
                    />
                  </div>
                </FormField>

                <FormField label="Jarak (km)">
                  <input
                    className="mp-input"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Contoh: 1.5"
                    value={place.distance_km || ""}
                    onChange={(e) =>
                      updateNearbyPlace(index, "distance_km", e.target.value)
                    }
                  />
                </FormField>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn-outline-mp"
          onClick={addNearbyPlace}
          style={{ marginTop: "12px" }}
        >
          <MdAdd size={16} />
          Tambah Lokasi Terdekat
        </button>
      </div>

      <PropertyImageUploader
        images={propertyImages}
        onChange={setPropertyImages}
        error={imageError}
        disabled={loading}
        maxFiles={10}
      />
    </StepSection>
  );
};

export default Step2PriceSpecs;
